import { useEffect } from 'react'

import type { HppTheme } from '@/lib/types'

/**
 * Applies a merchant theme as inline overrides on `:root` for the brand
 * CSS variables seeded in `src/styles/globals.css`. The existing token chain
 * propagates the override through shadcn semantic tokens (primary/secondary/
 * accent/ring) and, via `src/styles/adyen-overrides.css`, into Adyen's iframe
 * card form for the interactive accent.
 *
 * Restores prior inline values on cleanup so a re-fetch (or unmount) does
 * not leak the previous merchant's palette into a subsequent session.
 */
export function useHppTheme(theme: HppTheme | null): void {
  useEffect(() => {
    if (!theme) return
    const root = document.documentElement
    const previousPrimary = root.style.getPropertyValue('--color-brand-primary')
    const previousSecondary = root.style.getPropertyValue('--color-brand-secondary')
    root.style.setProperty('--color-brand-primary', theme.primaryColor)
    root.style.setProperty('--color-brand-secondary', theme.secondaryColor)
    return () => {
      if (previousPrimary) {
        root.style.setProperty('--color-brand-primary', previousPrimary)
      } else {
        root.style.removeProperty('--color-brand-primary')
      }
      if (previousSecondary) {
        root.style.setProperty('--color-brand-secondary', previousSecondary)
      } else {
        root.style.removeProperty('--color-brand-secondary')
      }
    }
  }, [theme])
}
